import { Contract } from "../../../../models/Entities/Contracts/Contract";
import { ContractConcept } from "../../../../models/Entities/Contracts/ContractConcept";
import { ContractAddendum } from "../../../../models/Entities/Contracts/ContractAddendum";
import { ErrorType } from "../../../../models/HttpError";
import { formatCurrencyAmount as formatAmountWithCurrency } from "../../../Practitioners/PractitionerConsumptions/PractitionerConsumptionCurrency";

// stores
import * as ContractConceptStore from '../../../../redux/middleware/ContractConceptMiddleware';
import * as ContractConceptPaymentScheduleStore from '../../../../redux/middleware/ContractConceptPaymentScheduleMiddleware';
import * as ContractStageStore from '../../../../redux/middleware/ContractStageMiddleware';

// state
import { AppState } from "../../../../redux/reducers";

// ──────────────────────────────────────────────────────────
// Enums (mirrored from backend)
// ──────────────────────────────────────────────────────────
export enum SettlementOptionsEnum {
    IncludeOnFirstSettlement = 1,
    IncludeOnLastSettlement = 2,
    IncludeOnEachSettlement = 3,
    ByStage = 4,
    IncludeByPeriod = 5,
    InAnyStage = 6,
    ByPatient = 7,
    Manual = 8,
    ByAddendum = 9,
    ByConcept = 10
}

export enum ConceptValuationOptionsEnum {
    FixedValue = 1,
    CostValue = 2,
    PercentageValue = 3,
    ValueByPriceList = 4,
    ValueByStage = 5
}

export type ConceptGroupKey = 'byStage' | 'inAnyStage' | 'others';

// A concept settled in ANY stage but grouped within the visit is billed together
// with each visit, so it belongs in the per-visit grid even though it has no
// per-stage payment schedule.
export const isGroupedInAnyStage = (cc: ContractConcept): boolean =>
    cc.settlementSetting === SettlementOptionsEnum.InAnyStage && !!cc.groupWithInTheVisit;

export const getGroupForConcept = (cc: ContractConcept): ConceptGroupKey => {
    if (
        cc.settlementSetting === SettlementOptionsEnum.ByStage ||
        cc.valuationType === ConceptValuationOptionsEnum.ValueByStage ||
        isGroupedInAnyStage(cc)
    ) {
        return 'byStage';
    }
    if (cc.settlementSetting === SettlementOptionsEnum.InAnyStage) {
        return 'inAnyStage';
    }
    return 'others';
};

export const getSettlementLabel = (value?: number): string => {
    switch (value) {
        case SettlementOptionsEnum.IncludeOnFirstSettlement: return 'Primera liquidación';
        case SettlementOptionsEnum.IncludeOnLastSettlement: return 'Última liquidación';
        case SettlementOptionsEnum.IncludeOnEachSettlement: return 'Cada liquidación';
        case SettlementOptionsEnum.ByStage: return 'Por etapa';
        case SettlementOptionsEnum.IncludeByPeriod: return 'Por período';
        case SettlementOptionsEnum.InAnyStage: return 'En cualquier etapa';
        case SettlementOptionsEnum.ByPatient: return 'Por paciente';
        case SettlementOptionsEnum.Manual: return 'Manual';
        case SettlementOptionsEnum.ByAddendum: return 'Por adenda';
        case SettlementOptionsEnum.ByConcept: return 'Por concepto';
        default: return '—';
    }
};

export const getValuationLabel = (value?: number): string => {
    switch (value) {
        case ConceptValuationOptionsEnum.FixedValue: return 'Valor fijo';
        case ConceptValuationOptionsEnum.CostValue: return 'Por costo';
        case ConceptValuationOptionsEnum.PercentageValue: return 'Porcentaje';
        case ConceptValuationOptionsEnum.ValueByPriceList: return 'Lista de precios';
        case ConceptValuationOptionsEnum.ValueByStage: return 'Valor por etapa';
        default: return '—';
    }
};

export const isFixedValueByStage = (cc: ContractConcept): boolean => {
    return cc.settlementSetting === SettlementOptionsEnum.ByStage
        && cc.valuationType === ConceptValuationOptionsEnum.ValueByStage
        || (cc.valuationType === ConceptValuationOptionsEnum.ValueByStage
            && cc.settlementSetting === SettlementOptionsEnum.IncludeOnEachSettlement)
        // any combination of FixedValue + ValueByStage according to spec
        || (cc.valuationType === ConceptValuationOptionsEnum.ValueByStage
            && cc.settlementSetting === SettlementOptionsEnum.ByStage);
};

// FixedValue + ValueByStage detection per spec wording
export const isFixedAndValueByStage = (cc: ContractConcept): boolean => {
    return cc.valuationType === ConceptValuationOptionsEnum.ValueByStage;
};

// Delegates to the shared consumption formatter: it normalises aliases ($, PESOS…)
// and, unlike the previous local version, does NOT pretend an unknown currency is
// ARS — an amount with no currency is rendered as a plain number.
export const formatCurrencyAmount = (amount: number | undefined | null, currency: string | undefined): string =>
    formatAmountWithCurrency(amount, currency);

// Resolution chain for any amount shown in the grid:
//   payment schedule currency → concept currency → contract currency
export const resolveAmountCurrency = (
    ...candidates: (string | undefined | null)[]
): string | undefined => {
    for (const candidate of candidates) {
        const normalized = (candidate || '').trim();
        if (normalized) return normalized;
    }
    return undefined;
};

// Importe sin símbolo de moneda, para la línea de factores que va debajo del
// total de la celda: la moneda ya la enuncia el total, repetirla en cada factor
// rompe la alineación decimal y no agrega nada.
export const formatPlainAmount = (value: number | undefined | null): string => {
    if (value === undefined || value === null || Number.isNaN(value)) return '—';
    return value.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

export const formatCoefficient = (value: number | undefined | null): string => {
    if (value === undefined || value === null || Number.isNaN(value)) return '—';
    if (value === 0) return '0';
    return value.toLocaleString('es-AR', { minimumFractionDigits: 0, maximumFractionDigits: 4 });
};

// ──────────────────────────────────────────────────────────
// Redux Props (same shape as ConceptList)
// ──────────────────────────────────────────────────────────
interface IProps {
    ContractId: number;
    ContractAddendumId: number;
}

interface ConnectedProps {
    isLoading: boolean;
    isDeleting: boolean;
    isLoadingSuccessfully: boolean;
    failOnLoading: boolean;
    failOnDeleting: boolean;
    error: ErrorType | undefined;
    Contract: Contract | undefined;
    Addendum: ContractAddendum | undefined;
    ContractConcepts: ContractConcept[];
}

interface DispatchProps {
    GetAllContractsConceptByAddendum: typeof ContractConceptStore.actionCreators.GetAllContractsConceptByAddendum;
    DeleteContractConcept: typeof ContractConceptStore.actionCreators.DeleteContractConcept;
    GetAllContractsConceptPaymentSchedule: typeof ContractConceptPaymentScheduleStore.actionCreators.GetAllContractsConceptPaymentSchedule;
    GetAllContractStagesByAddendumId: typeof ContractStageStore.actionCreators.GetAllContractStagesByAddendumId;
}

export type Props = IProps & ConnectedProps & DispatchProps;

export const mapStateToProps = (state: AppState, ownProps: IProps) => {
    const CurrentContract = state.Contract?.list?.find((item: Contract) => item.id === ownProps.ContractId);
    const CurrentAddendum = CurrentContract?.addendums?.find((item: any) => item.id === ownProps.ContractAddendumId);

    // Schedules can land in TWO places depending on the dispatched action:
    //  - RECEIVE_ACTION   → addendum.concepts[].paymentSchedule
    //  - UPDATE_ALL action → contract.concepts[].paymentSchedule
    // Merge both so the matrix always sees the latest schedule.
    const contractLevelConcepts = (CurrentContract as any)?.concepts as ContractConcept[] | undefined;
    const mergedConcepts: ContractConcept[] = (CurrentAddendum?.concepts || []).map((cc: ContractConcept) => {
        const addendumSchedule = cc.paymentSchedule;
        if (Array.isArray(addendumSchedule) && addendumSchedule.length > 0) return cc;
        const fromContract = contractLevelConcepts?.find((x: ContractConcept) => x.id === cc.id);
        if (fromContract?.paymentSchedule && fromContract.paymentSchedule.length > 0) {
            return { ...cc, paymentSchedule: fromContract.paymentSchedule };
        }
        return cc;
    });

    return {
        isLoading: CurrentAddendum?.isGettingContractConcepts || false,
        isLoadingSuccessfully: (!!CurrentAddendum && !CurrentAddendum?.isGettingContractConcepts && !CurrentAddendum?.FailGettingContractConcepts) || false,
        failOnLoading: CurrentAddendum?.FailGettingContractConcepts || false,
        isDeleting: false,
        failOnDeleting: false,
        error: CurrentAddendum?.ErrorGettingContractConcepts || undefined,
        Contract: CurrentContract,
        Addendum: CurrentAddendum,
        // preserve load order — DO NOT sort here; consumer toggles sort
        ContractConcepts: mergedConcepts,
    };
};

export const mapDispatchToProps = {
    ...ContractConceptStore.actionCreators,
    ...ContractConceptPaymentScheduleStore.actionCreators,
    ...ContractStageStore.actionCreators,
};
